import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import memoryService from '../../services/memoryService';
import { Check, X, Edit2, Trash2, Pin, PinOff, Plus, AlertTriangle } from 'lucide-react';

const MemorySection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [memoryItems, setMemoryItems] = useState([]);
  const [blockedTopics, setBlockedTopics] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [newBlockedTopic, setNewBlockedTopic] = useState('');
  const [showAddMemory, setShowAddMemory] = useState(false);
  const [newMemory, setNewMemory] = useState({ title: '', content: '', item_type: 'note' });

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [items, topics] = await Promise.all([
        memoryService.getMemoryItems(user.id),
        memoryService.getBlockedTopics(user.id)
      ]);
      
      setMemoryItems(items);
      setBlockedTopics(topics);
    } catch (error) {
      console.error('Error loading memory data:', error);
      setMessage({ type: 'error', text: 'Failed to load memory data' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMemory = async () => {
    if (!newMemory.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    try {
      await memoryService.createMemoryItem(user.id, newMemory);
      await loadData();
      setShowAddMemory(false);
      setNewMemory({ title: '', content: '', item_type: 'note' });
      setMessage({ type: 'success', text: 'Memory added successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error adding memory:', error);
      setMessage({ type: 'error', text: 'Failed to add memory' });
    }
  };

  const handleUpdateMemory = async (itemId, updates) => {
    try {
      await memoryService.updateMemoryItem(itemId, updates);
      await loadData();
      setEditingItem(null);
      setMessage({ type: 'success', text: 'Memory updated successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating memory:', error);
      setMessage({ type: 'error', text: 'Failed to update memory' });
    }
  };

  const handleDeleteMemory = async (itemId, title) => {
    if (!confirm(`Delete memory "${title}"?`)) return;
    
    try {
      await memoryService.deleteMemoryItem(itemId);
      await loadData();
      setMessage({ type: 'success', text: 'Memory deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting memory:', error);
      setMessage({ type: 'error', text: 'Failed to delete memory' });
    }
  };

  const handleTogglePin = async (itemId, currentPinStatus) => {
    try {
      await memoryService.togglePinMemoryItem(itemId, !currentPinStatus);
      await loadData();
    } catch (error) {
      console.error('Error toggling pin:', error);
      setMessage({ type: 'error', text: 'Failed to pin/unpin memory' });
    }
  };

  const handleAddBlockedTopic = async () => {
    if (!newBlockedTopic.trim()) return;

    try {
      await memoryService.addBlockedTopic(user.id, newBlockedTopic.trim());
      await loadData();
      setNewBlockedTopic('');
      setMessage({ type: 'success', text: 'Topic blocked successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error blocking topic:', error);
      setMessage({ type: 'error', text: 'Failed to block topic' });
    }
  };

  const handleRemoveBlockedTopic = async (topicId) => {
    try {
      await memoryService.removeBlockedTopic(topicId);
      await loadData();
    } catch (error) {
      console.error('Error removing blocked topic:', error);
      setMessage({ type: 'error', text: 'Failed to remove blocked topic' });
    }
  };

  const handleClearAllMemory = async () => {
    if (!confirm('Are you sure you want to clear ALL memory items? This cannot be undone.')) return;
    
    try {
      await memoryService.clearAllMemory(user.id);
      await loadData();
      setMessage({ type: 'success', text: 'All memory cleared successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error clearing memory:', error);
      setMessage({ type: 'error', text: 'Failed to clear memory' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Memory</h2>
        <p className="text-gray-600 mt-1">
          Manage what Swirl remembers about you
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Memory Items Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Memory Items</h3>
          <button
            onClick={() => setShowAddMemory(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={18} />
            <span>Add Memory</span>
          </button>
        </div>

        {/* Add Memory Form */}
        {showAddMemory && (
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-900 mb-3">Add New Memory</h4>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Title"
                value={newMemory.title}
                onChange={(e) => setNewMemory(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <textarea
                placeholder="Content"
                value={newMemory.content}
                onChange={(e) => setNewMemory(prev => ({ ...prev, content: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <select
                value={newMemory.item_type}
                onChange={(e) => setNewMemory(prev => ({ ...prev, item_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="note">Note</option>
                <option value="fact">Fact</option>
                <option value="preference">Preference</option>
                <option value="context">Context</option>
              </select>
              <div className="flex space-x-2">
                <button
                  onClick={handleAddMemory}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowAddMemory(false);
                    setNewMemory({ title: '', content: '', item_type: 'note' });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Memory Items List */}
        {memoryItems.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-600">No memory items yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {memoryItems.map(item => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {item.item_type}
                      </span>
                      {item.is_pinned && (
                        <Pin size={14} className="text-purple-600" fill="currentColor" />
                      )}
                    </div>
                    {item.content && (
                      <p className="text-sm text-gray-600 mt-1">{item.content}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Added {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleTogglePin(item.id, item.is_pinned)}
                      className="p-2 text-gray-400 hover:text-purple-500 transition-colors"
                      title={item.is_pinned ? 'Unpin' : 'Pin'}
                    >
                      {item.is_pinned ? <PinOff size={18} /> : <Pin size={18} />}
                    </button>
                    <button
                      onClick={() => handleDeleteMemory(item.id, item.title)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Blocked Topics Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Blocked Topics</h3>
        <p className="text-sm text-gray-600">
          Swirl will avoid these topics in suggestions and recommendations
        </p>

        <div className="flex space-x-2">
          <input
            type="text"
            placeholder="Enter topic to block"
            value={newBlockedTopic}
            onChange={(e) => setNewBlockedTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddBlockedTopic()}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button
            onClick={handleAddBlockedTopic}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900"
          >
            Block
          </button>
        </div>

        {blockedTopics.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {blockedTopics.map(topic => (
              <div
                key={topic.id}
                className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full"
              >
                <span className="text-sm text-gray-700">{topic.topic}</span>
                <button
                  onClick={() => handleRemoveBlockedTopic(topic.id)}
                  className="text-gray-500 hover:text-red-500"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clear All Memory */}
      <div className="pt-6 border-t border-gray-200">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900 mb-2">Danger Zone</h4>
              <p className="text-sm text-red-800 mb-4">
                Clearing all memory will permanently delete all memory items. This action cannot be undone.
              </p>
              <button
                onClick={handleClearAllMemory}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Clear All Memory
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorySection;





